// Shared backend-error message extractor. Every KaziPeople service explains a
// refusal in its error body, but each stack has its own envelope:
//   Go services (payroll/credit/benefits/paygate/…):  { code, message }
//   FastAPI (employee/leave/ai-agent/compliance):     { detail }  — string, or a
//                                                     422 validation array
//   Gateway:                { message, statusCode } or { error: { code, message } }
//   NestJS (auth/billing):  { message } — string or string[]
// UI rule: a user-triggered action must never swallow that explanation — show
// getApiErrorMessage(e, fallback) in the failure toast. The fallback is used
// only when the body carries no usable text (network failure, HTML, empty).
import i18n from '@/i18n/config';
import { submissionAdvice } from '@/features/shared/components/recovery/submissionOutcome';

function firstString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (Array.isArray(v)) {
    for (const item of v) {
      // NestJS validation: string[]; FastAPI 422: [{ msg, loc, type }]
      const s = firstString(typeof item === 'object' && item !== null ? (item as any).msg : item);
      if (s) return s;
    }
    return null;
  }
  if (typeof v === 'object' && v !== null) {
    return firstString((v as any).message) ?? firstString((v as any).msg);
  }
  return null;
}

/**
 * True when the gateway couldn't confirm the workspace's access just now
 * (billing/auth unreachable): 503 with code `fallback_fail_closed` / mode
 * `UNAVAILABLE`. It is temporary — "try again in a moment" — never "not enabled
 * on your plan" (that is a 403 BLOCK) and never "being rolled out"
 * (DEF-0mvnigi2p001).
 */
export function isEntitlementUnavailable(e: unknown): boolean {
  const r = (e as any)?.response;
  if (r?.status !== 503) return false;
  const d = r?.data || {};
  return d.code === 'fallback_fail_closed' || d.mode === 'UNAVAILABLE';
}

/**
 * Extract the human-readable backend message from a failed request (axios or
 * fetch-style), falling back to the given generic copy.
 */
export function getApiErrorMessage(e: unknown, fallback?: string): string {
  const data = (e as any)?.response?.data ?? (e as any)?.data;
  const msg =
    firstString(data?.detail) ??
    firstString(data?.message) ??
    firstString(data?.error) ??
    // A plain-text (non-JSON) body that isn't an HTML error page.
    (typeof data === 'string' && data.trim() && !data.trim().startsWith('<') ? data.trim() : null);
  if (msg) return msg;
  return fallback ?? i18n.t('common:requestFailed', { defaultValue: 'Something went wrong. Please try again.' });
}

/**
 * The message for a submission that did not obviously succeed, for an action
 * where repeating it could move money or create a duplicate.
 *
 * ⚠️ WHY THIS IS NOT getApiErrorMessage. That helper answers "what did the
 * server say". When the server said NOTHING — no response, or a 5xx after it
 * had already been reached — there is nothing to quote and the fallback copy
 * usually asserts failure. For a payroll run, a disbursement or a loan
 * approval, "it failed, try again" invites doing it twice. An unknown payment
 * state is IN FLIGHT, never failed.
 *
 * Returns the server's own words when it gave any, and otherwise says what is
 * actually known. `action` tells the caller whether a Retry button is safe to
 * render at all.
 *
 * See recovery/submissionOutcome.ts for the 4xx/5xx reasoning.
 */
export function getSubmissionAdvice(
  e: unknown,
  opts: { consequential?: boolean } = {},
): { message: string; action: 'retry' | 'check'; outcome: 'rejected' | 'uncertain' } {
  const status = (e as any)?.response?.status as number | undefined;
  const advice = submissionAdvice(status, { consequential: opts.consequential });
  // A refusal the server explained is better than our generic sentence; an
  // uncertainty is ours to describe, because the server did not answer.
  const serverSaid = advice.outcome === 'rejected' ? getApiErrorMessage(e, '') : '';
  return {
    message: serverSaid || advice.message,
    action: advice.action,
    outcome: advice.outcome,
  };
}
